import { generatePagerProps } from '@fukuri-admins/utils/common/generatePagerProps';
import { getEnvFeatureFlag } from '~/utils/getEnvFeatureFlag';
import { useUserList } from './hooks';
import { convertToPresentationProps } from './logics';
import { UserListPresentation } from './presentations';
import { UserListEmptyPresentation } from './presentations/empty';

/**
 * 利用者一覧 Container
 *
 * - 状態管理・アクションは useUserList に委譲する
 * - API 型 → Presentation 型の変換は convertToPresentationProps に委譲する
 * - Empty / 通常表示の出し分けは isEmpty フラグで行う（三項演算子）
 * - 検索条件あり × 0 件のときは Empty を表示せず検索フォーム＋テーブルを表示する（PEN-309 対応パターン）
 * - isActivationCodeEnabled（= enableStaging || enableInternal）を Container で判定し、Presentation に渡す
 *
 * NOTE: 親コンポーネント（Screen）で Suspense にラップすること
 */
export const UserList: React.FC = () => {
  const { enableStaging, enableInternal } = getEnvFeatureFlag();
  const isActivationCodeEnabled = enableStaging || enableInternal;

  const {
    state: {
      usersData,
      totalPage,
      totalCount,
      currentPage,
      fullNameValue,
      emailValue,
      clientNameValue,
      phoneNumberValue,
      activationCodeValue,
      isFiltered,
    },
    actions: {
      handleClickAddUserByEmail,
      handleClickAddUserByActivationCode,
      handlePageChange,
      handleRowClick,
      handleDeleteUser,
      handleEditUser,
      handleCopyActivationCode,
      handleFilterChange,
      handleSearch,
      handleReset,
    },
  } = useUserList();

  const { rows, isEmpty } = convertToPresentationProps({ usersData, isFiltered });

  const pagerProps = generatePagerProps({
    currentPage,
    totalPage,
    totalCount,
    rows,
    onPageChange: handlePageChange,
  });

  return isEmpty ? (
    <UserListEmptyPresentation
      isActivationCodeEnabled={isActivationCodeEnabled}
      onClickAddUserByEmail={handleClickAddUserByEmail}
      onClickAddUserByActivationCode={handleClickAddUserByActivationCode}
    />
  ) : (
    <UserListPresentation
      rows={rows}
      isActivationCodeEnabled={isActivationCodeEnabled}
      onEditUser={handleEditUser}
      onDeleteUser={handleDeleteUser}
      onRowClick={handleRowClick}
      pagerProps={pagerProps}
      onClickAddUserByEmail={handleClickAddUserByEmail}
      onClickAddUserByActivationCode={handleClickAddUserByActivationCode}
      fullNameValue={fullNameValue}
      emailValue={emailValue}
      clientNameValue={clientNameValue}
      phoneNumberValue={phoneNumberValue}
      activationCodeValue={activationCodeValue}
      onFullNameChange={handleFilterChange('full_name')}
      onEmailChange={handleFilterChange('email')}
      onClientNameChange={handleFilterChange('client_name')}
      onPhoneNumberChange={handleFilterChange('phone_number')}
      onActivationCodeChange={handleFilterChange('activation_code')}
      onSearch={handleSearch}
      onReset={handleReset}
      onCopyActivationCode={handleCopyActivationCode}
    />
  );
};
